/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import React, { useMemo, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { Alert, Button as RsButton, Modal, ModalBody, ModalHeader } from "reactstrap";

import useConfig from "../../../actions/Tenants/config/configHook";
import { useIsMobile } from "../../../hooks";
import { Loading } from "../../Helper/Loading";
import FullPageLayout from "../Layout/FullPageLayout";

import SignalBulkBar from "./components/SignalBulkBar";
import SignalFilterRail from "./components/SignalFilterRail";
import SignalWorklist from "./components/SignalWorklist";
import SignalDetailModal from "./SignalDetailModal";
import { DEFAULT_FILTER_STATE, filterAndSortSignals, SignalFilterState } from "./signalFiltering";
import { useSignals, useUpdateSignal } from "./signalHooks";
import { useActivateSignal } from "./useActiveSignal";
import { useSignalSelection } from "./useSignalSelection";
import { getCellRelation, getPalletRelations, Signal } from "./signalTypes";

const distinctOptions = (signals: Signal[], pick: (s: Signal) => { value?: string; label?: string } | undefined) => {
    const seen = new Map<string, string>();
    signals.forEach((s) => {
        const option = pick(s);
        if (option?.value && !seen.has(option.value)) seen.set(option.value, option.label || option.value);
    });
    return Array.from(seen.entries()).map(([value, label]) => ({ value, label }));
};

export const SignalsPage: React.FC<{ isInModal?: boolean }> = ({ isInModal = false }) => {
    const isMobile = useIsMobile(1200);
    const { location_id, layer_id } = useParams();
    const [searchParams] = useSearchParams();
    // Pre-filter to a cell when arriving from a room's traffic-light dot.
    const cellParam = searchParams.get("facility_location_id");

    const [filterState, setFilterState] = useState<SignalFilterState>({
        ...DEFAULT_FILTER_STATE,
        cell: location_id ?? cellParam ?? null,
        pallet: layer_id ?? null,
    });
    // D4 fallback ONLY: a signal with no cell relation has nowhere to
    // navigate to, so it opens here instead. Every row WITH a room activates
    // the banner via `activate` below and never touches this state.
    const [selectedSignalId, setSelectedSignalId] = useState<string | null>(null);
    const [showFilters, setShowFilters] = useState(false);
    const selection = useSignalSelection();
    const activate = useActivateSignal();

    const { data, isLoading, isError } = useSignals({ limit: 200 });
    const signals = useMemo(() => data?.results ?? [], [data]);

    const visible = useMemo(() => filterAndSortSignals(signals, filterState), [signals, filterState]);
    const cellOptions = useMemo(
        () => distinctOptions(signals, (s) => {
            const cell = getCellRelation(s);
            return cell ? { value: cell.facility_location_id, label: cell.facility_location_label || cell.facility_location_id } : undefined;
        }),
        [signals],
    );
    const palletOptions = useMemo(
        () => distinctOptions(signals, (s) => {
            const pallet = getPalletRelations(s)[0];
            return pallet ? { value: pallet.layer_id, label: pallet.layer_label || pallet.layer_id } : undefined;
        }),
        [signals],
    );

    const updateSignal = useUpdateSignal();
    const selectable = filterState.view === "open";
    const selectedSignal = signals.find((s) => s.id === selectedSignalId) ?? null;

    const patchFilter = (patch: Partial<SignalFilterState>) => setFilterState((prev) => ({ ...prev, ...patch }));

    // Serves all three of this component's mount points — the standalone
    // `/signals` page, `/signals/view/:view`, and the room's own
    // `SignalInboxModal` — because they all render this same component.
    const handleRowActivate = (signal: Signal) => activate(signal, (noRoomSignal) => setSelectedSignalId(noRoomSignal.id));

    const handleResolveReject = async (signalId: string, status: "resolved" | "rejected", note?: string) => {
        await updateSignal.mutateAsync({ signalId, status, note });
        if (selectedSignalId === signalId) setSelectedSignalId(null);
    };

    const handleBulk = async (status: "resolved" | "rejected", note?: string) => {
        // Parallel PUTs — each signal writes its own audit event (no bulk endpoint).
        await Promise.all(selection.selectedIds.map((signalId) => updateSignal.mutateAsync({ signalId, status, note })));
        selection.clear();
    };

    const rail = (
        <SignalFilterRail state={filterState} onChange={patchFilter} cellOptions={cellOptions} palletOptions={palletOptions} />
    );

    if (isError) {
        return <Alert color="danger">Failed to load signals. Please try again.</Alert>;
    }

    // When embedded in a modal (room-detail inbox), always show the rail inline.
    const inlineRail = !isMobile || isInModal;

    return (
        <div className="d-flex flex-column gap-3 p-2 p-lg-3">
            {!inlineRail && (
                <RsButton color="secondary" outline onClick={() => setShowFilters(true)}>Filters</RsButton>
            )}

            <SignalBulkBar
                count={selection.count}
                onResolveReject={handleBulk}
                onClear={selection.clear}
                isSubmitting={updateSignal.isPending}
            />

            <div className="d-flex gap-4">
                {inlineRail && (
                    <div css={css`width: 260px; flex-shrink: 0;`}>{rail}</div>
                )}

                <div className="flex-grow-1" css={css`min-width: 0;`}>
                    {isLoading ? (
                        <Loading />
                    ) : (
                        <SignalWorklist
                            signals={visible}
                            activeSignalId={selectedSignalId ?? undefined}
                            onRowClick={handleRowActivate}
                            selection={selection}
                            selectable={selectable}
                        />
                    )}
                </div>
            </div>

            {/* Mobile filters drawer (reactstrap Modal — no Offcanvas in this app). */}
            <Modal isOpen={!inlineRail && showFilters} toggle={() => setShowFilters(false)}>
                <ModalHeader toggle={() => setShowFilters(false)}>Filters</ModalHeader>
                <ModalBody>{rail}</ModalBody>
            </Modal>

            <SignalDetailModal
                signal={selectedSignal}
                isOpen={!!selectedSignal}
                onClose={() => setSelectedSignalId(null)}
                onResolveReject={handleResolveReject}
                isSubmitting={updateSignal.isPending}
            />
        </div>
    );
};

export default function SignalPageWrapped() {
    const config = useConfig();
    // On a hard page load the tenant configs are still fetching for the first
    // render(s); root_config is {} then, NOT "flag off". Bouncing on that
    // half-loaded config sent valid /signals deep-links to the tenant home —
    // a race that e2e signals specs hit under load. Wait for the fetch.
    if (!config.configs_loaded) {
        return <Loading />;
    }
    // Master gate (EFQ-4145): the standalone Signals page is unreachable for
    // tenants without the feature, even by deep-link (the nav link is hidden
    // elsewhere). Bounce to the tenant home.
    if (!config.root_config?.ripening_room_enable_signals) {
        return <Navigate to="/" replace />;
    }
    return (
        <FullPageLayout>
            <SignalsPage />
        </FullPageLayout>
    );
}
